import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import adminApi from '../api/client';

type ReportStatus = 'pending' | 'reviewed' | 'dismissed' | 'actioned';
type TargetType = 'post' | 'comment';
type TargetStatus = 'active' | 'hidden' | 'deleted' | 'unavailable';
type ReviewAction = 'dismiss' | 'hide' | 'restore';
type OfficialType = 'author' | 'publisher';

interface PersonSummary {
  user_id: string;
  display_name: string;
  avatar_url: string | null;
}

interface CircleSummary {
  club_id: string;
  official_type: OfficialType;
  subject_id: string;
  display_name: string;
  name_sinhala: string | null;
  state: 'draft' | 'published' | 'paused' | 'retired';
}

interface TargetSummary {
  target_type: TargetType;
  target_id: string;
  status: TargetStatus;
  body: string | null;
  post_id: string | null;
  parent_comment_id: string | null;
  author: PersonSummary | null;
  deleted_at: string | null;
  moderated_at: string | null;
  moderated_by: string | null;
  moderation_reason: string | null;
}

interface CommunityReport {
  report_id: string;
  circle: CircleSummary;
  target: TargetSummary | null;
  reporter: PersonSummary;
  target_type: TargetType;
  target_id: string;
  reason: string;
  status: ReportStatus;
  moderation_reason: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
}

interface ReportQueue {
  items: CommunityReport[];
  page: number;
  per_page: number;
  total: number;
}

interface ReviewRequest {
  action: ReviewAction;
  note: string;
  expected_status: 'pending' | 'actioned';
}

interface ApiErrorShape {
  response?: {
    status?: number;
  };
}

const PAGE_SIZE = 20;
const MAX_NOTE_LENGTH = 500;
const REPORT_STATUSES: ReportStatus[] = ['pending', 'reviewed', 'dismissed', 'actioned'];
const TARGET_TYPES: TargetType[] = ['post', 'comment'];
const TARGET_STATUSES: TargetStatus[] = ['active', 'hidden', 'deleted', 'unavailable'];

class MalformedCommunityResponseError extends Error {
  constructor() {
    super('Malformed Official Circle Community moderation response');
    this.name = 'MalformedCommunityResponseError';
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function requiredString(value: unknown): string {
  if (typeof value !== 'string' || !value.trim()) {
    throw new MalformedCommunityResponseError();
  }
  return value;
}

function nullableString(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string') throw new MalformedCommunityResponseError();
  return value;
}

function timestamp(value: unknown): string {
  const parsed = requiredString(value);
  if (!Number.isFinite(Date.parse(parsed))) {
    throw new MalformedCommunityResponseError();
  }
  return parsed;
}

function nullableTimestamp(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  return timestamp(value);
}

function oneOf<T extends string>(value: unknown, values: readonly T[]): T {
  if (typeof value !== 'string' || !values.includes(value as T)) {
    throw new MalformedCommunityResponseError();
  }
  return value as T;
}

function parsePerson(value: unknown): PersonSummary {
  if (!isRecord(value)) throw new MalformedCommunityResponseError();
  return {
    user_id: requiredString(value.user_id),
    display_name: requiredString(value.display_name),
    avatar_url: nullableString(value.avatar_url),
  };
}

function parseCircle(value: unknown): CircleSummary {
  if (!isRecord(value)) throw new MalformedCommunityResponseError();
  return {
    club_id: requiredString(value.club_id),
    official_type: oneOf(value.official_type, ['author', 'publisher']),
    subject_id: requiredString(value.subject_id),
    display_name: requiredString(value.display_name),
    name_sinhala: nullableString(value.name_sinhala),
    state: oneOf(value.state, ['draft', 'published', 'paused', 'retired']),
  };
}

function parseTarget(value: unknown, expectedType: TargetType): TargetSummary | null {
  if (value === null || value === undefined) return null;
  if (!isRecord(value)) throw new MalformedCommunityResponseError();
  const targetType = oneOf(value.target_type, TARGET_TYPES);
  if (targetType !== expectedType) throw new MalformedCommunityResponseError();
  return {
    target_type: targetType,
    target_id: requiredString(value.target_id),
    status: oneOf(value.status, TARGET_STATUSES),
    body: nullableString(value.body),
    post_id: nullableString(value.post_id),
    parent_comment_id: nullableString(value.parent_comment_id),
    author: value.author === null || value.author === undefined ? null : parsePerson(value.author),
    deleted_at: nullableTimestamp(value.deleted_at),
    moderated_at: nullableTimestamp(value.moderated_at),
    moderated_by: nullableString(value.moderated_by),
    moderation_reason: nullableString(value.moderation_reason),
  };
}

export function parseCommunityReport(value: unknown): CommunityReport {
  if (!isRecord(value)) throw new MalformedCommunityResponseError();
  const targetType = oneOf(value.target_type, TARGET_TYPES);
  return {
    report_id: requiredString(value.report_id),
    circle: parseCircle(value.circle),
    target: parseTarget(value.target, targetType),
    reporter: parsePerson(value.reporter),
    target_type: targetType,
    target_id: requiredString(value.target_id),
    reason: requiredString(value.reason),
    status: oneOf(value.status, REPORT_STATUSES),
    moderation_reason: nullableString(value.moderation_reason),
    resolved_by: nullableString(value.resolved_by),
    resolved_at: nullableTimestamp(value.resolved_at),
    created_at: timestamp(value.created_at),
    updated_at: timestamp(value.updated_at),
  };
}

export function parseCommunityReportQueue(value: unknown): ReportQueue {
  if (!isRecord(value) || !Array.isArray(value.items)) {
    throw new MalformedCommunityResponseError();
  }
  if (
    !Number.isInteger(value.page) ||
    !Number.isInteger(value.per_page) ||
    !Number.isInteger(value.total) ||
    Number(value.page) < 1 ||
    Number(value.per_page) < 1 ||
    Number(value.per_page) > 50 ||
    Number(value.total) < 0
  ) {
    throw new MalformedCommunityResponseError();
  }
  return {
    items: value.items.map((item) => parseCommunityReport(item)),
    page: Number(value.page),
    per_page: Number(value.per_page),
    total: Number(value.total),
  };
}

function statusOf(error: unknown): number | undefined {
  return (error as ApiErrorShape).response?.status;
}

function safeErrorMessage(error: unknown, surface: 'queue' | 'detail' | 'review'): string {
  if (error instanceof MalformedCommunityResponseError) {
    return 'The server returned an invalid moderation response. Retry or contact an administrator.';
  }
  const status = statusOf(error);
  if (status === 401) return 'Your admin session expired. Please sign in again.';
  if (status === 403) return 'This admin session is not permitted to use Community moderation.';
  if (status === 404) return surface === 'detail'
    ? 'This report or its target is no longer available. Refresh the queue.'
    : 'The Community moderation queue is unavailable.';
  if (status === 409) return 'This report changed by another admin. The latest state was loaded; review it again.';
  if (status === 422) return 'The moderation request did not pass validation.';
  if (status === 500) return 'Community moderation failed safely. Retry if appropriate.';
  return 'The Community moderation service could not be reached. Retry if appropriate.';
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function label(value: string): string {
  return value.replace(/_/g, ' ');
}

function targetStatusClass(status: TargetStatus): string {
  if (status === 'active') return 'bg-emerald-50 text-emerald-800 border-emerald-200';
  if (status === 'hidden') return 'bg-amber-50 text-amber-800 border-amber-200';
  if (status === 'deleted') return 'bg-gray-100 text-gray-700 border-gray-300';
  return 'bg-red-50 text-red-800 border-red-200';
}

const OfficialCircleCommunityModeration: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<ReportStatus>('pending');
  const [targetTypeFilter, setTargetTypeFilter] = useState<TargetType | ''>('');
  const [circleIdDraft, setCircleIdDraft] = useState('');
  const [circleIdFilter, setCircleIdFilter] = useState('');
  const [page, setPage] = useState(1);
  const [queue, setQueue] = useState<ReportQueue | null>(null);
  const [queueLoading, setQueueLoading] = useState(true);
  const [queueError, setQueueError] = useState<string | null>(null);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [detail, setDetail] = useState<CommunityReport | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [reviewAction, setReviewAction] = useState<ReviewAction | null>(null);
  const [reviewNote, setReviewNote] = useState('');
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const queueRequest = useRef(0);
  const detailRequest = useRef(0);

  const fetchQueue = useCallback(async () => {
    const requestId = ++queueRequest.current;
    setQueueLoading(true);
    setQueueError(null);
    const params: Record<string, string | number> = {
      status: statusFilter,
      page,
      per_page: PAGE_SIZE,
    };
    if (targetTypeFilter) params.target_type = targetTypeFilter;
    if (circleIdFilter) params.circle_id = circleIdFilter;
    try {
      const response = await adminApi.get('/admin/official-circles/community/reports', { params });
      const parsed = parseCommunityReportQueue(response.data);
      if (requestId !== queueRequest.current) return;
      setQueue(parsed);
    } catch (error) {
      if (requestId !== queueRequest.current) return;
      setQueue(null);
      setQueueError(safeErrorMessage(error, 'queue'));
    } finally {
      if (requestId === queueRequest.current) setQueueLoading(false);
    }
  }, [circleIdFilter, page, statusFilter, targetTypeFilter]);

  const fetchDetail = useCallback(async (reportId: string) => {
    const requestId = ++detailRequest.current;
    setDetailLoading(true);
    setDetailError(null);
    try {
      const response = await adminApi.get(
        `/admin/official-circles/community/reports/${encodeURIComponent(reportId)}`,
      );
      const parsed = parseCommunityReport(response.data);
      if (requestId !== detailRequest.current) return;
      setDetail(parsed);
    } catch (error) {
      if (requestId !== detailRequest.current) return;
      setDetail(null);
      setDetailError(safeErrorMessage(error, 'detail'));
    } finally {
      if (requestId === detailRequest.current) setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchQueue();
    return () => {
      queueRequest.current += 1;
    };
  }, [fetchQueue]);

  useEffect(() => {
    if (!selectedReportId) {
      setDetail(null);
      setDetailError(null);
      return () => undefined;
    }
    void fetchDetail(selectedReportId);
    return () => {
      detailRequest.current += 1;
    };
  }, [fetchDetail, selectedReportId]);

  const selectReport = (reportId: string) => {
    setSelectedReportId(reportId);
    setReviewAction(null);
    setReviewConfirmed(false);
    setReviewError(null);
    setSuccessMessage(null);
  };

  const applyCircleFilter = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPage(1);
    setCircleIdFilter(circleIdDraft.trim());
  };

  const clearFilters = () => {
    setStatusFilter('pending');
    setTargetTypeFilter('');
    setCircleIdDraft('');
    setCircleIdFilter('');
    setPage(1);
  };

  const openReview = (action: ReviewAction) => {
    setReviewAction(action);
    setReviewConfirmed(false);
    setReviewError(null);
    setSuccessMessage(null);
  };

  const closeReview = () => {
    if (reviewSubmitting) return;
    setReviewAction(null);
    setReviewNote('');
    setReviewConfirmed(false);
    setReviewError(null);
  };

  const refreshAfterAction = async (reportId: string) => {
    await Promise.all([fetchQueue(), fetchDetail(reportId)]);
  };

  const submitReview = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!detail || !reviewAction || reviewSubmitting) return;
    const note = reviewNote.trim();
    if (!note) {
      setReviewError('Enter a nonblank moderation note before confirming.');
      return;
    }
    if (note.length > MAX_NOTE_LENGTH) {
      setReviewError(`Moderation note must be ${MAX_NOTE_LENGTH} characters or fewer.`);
      return;
    }
    if (!reviewConfirmed) {
      setReviewError('Confirm the moderation action before submitting.');
      return;
    }
    setReviewSubmitting(true);
    setReviewError(null);
    setSuccessMessage(null);
    const payload: ReviewRequest = {
      action: reviewAction,
      note,
      expected_status: detail.status as 'pending' | 'actioned',
    };
    try {
      await adminApi.post(
        `/admin/official-circles/community/reports/${encodeURIComponent(detail.report_id)}/review`,
        payload,
      );
      await refreshAfterAction(detail.report_id);
      setReviewAction(null);
      setReviewNote('');
      setReviewConfirmed(false);
      setSuccessMessage('Moderation action completed and the latest queue state was loaded.');
    } catch (error) {
      if (statusOf(error) === 409) {
        setReviewError('This report changed by another admin. Refreshing the queue and report; review again.');
        await refreshAfterAction(detail.report_id);
      } else {
        setReviewError(safeErrorMessage(error, 'review'));
      }
    } finally {
      setReviewSubmitting(false);
    }
  };

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((queue?.total || 0) / (queue?.per_page || PAGE_SIZE))),
    [queue],
  );
  const canDismiss = detail?.status === 'pending';
  const canHide = detail?.status === 'pending' && detail.target?.status === 'active';
  const canRestore = detail?.status === 'actioned' && detail.target?.status === 'hidden';

  return (
    <div className="min-h-screen bg-[#F1EFE8] p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A6A38]">Official Circles</p>
          <h1 className="mt-2 text-3xl font-bold text-[#085041]">Community moderation</h1>
          <p className="mt-2 max-w-3xl text-sm text-gray-600">
            Review reports for dedicated Official Circle Community posts and comments. Actions are server-authorized,
            scoped to the reported target, and recorded for audit.
          </p>
        </header>

        <section aria-labelledby="community-report-filters" className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 id="community-report-filters" className="text-lg font-semibold text-gray-800">Report queue</h2>
              <p className="mt-1 text-sm text-gray-500">Pending reports are shown by default.</p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-sm font-medium text-gray-700">
                Status
                <select
                  aria-label="Community report status filter"
                  value={statusFilter}
                  onChange={(event) => { setPage(1); setStatusFilter(event.target.value as ReportStatus); }}
                  className="mt-1 block rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                >
                  {REPORT_STATUSES.map((value) => <option key={value} value={value}>{label(value)}</option>)}
                </select>
              </label>
              <label className="text-sm font-medium text-gray-700">
                Target type
                <select
                  aria-label="Community report target type filter"
                  value={targetTypeFilter}
                  onChange={(event) => { setPage(1); setTargetTypeFilter(event.target.value as TargetType | ''); }}
                  className="mt-1 block rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="">All targets</option>
                  {TARGET_TYPES.map((value) => <option key={value} value={value}>{value}</option>)}
                </select>
              </label>
              <form onSubmit={applyCircleFilter}>
                <label className="text-sm font-medium text-gray-700">
                  Circle ID
                  <input
                    aria-label="Community Circle ID filter"
                    value={circleIdDraft}
                    onChange={(event) => setCircleIdDraft(event.target.value)}
                    className="mt-1 block w-48 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    placeholder="Optional ID"
                  />
                </label>
              </form>
              <button
                type="button"
                onClick={clearFilters}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:border-[#085041]"
              >
                Clear filters
              </button>
            </div>
          </div>
        </section>

        {queueError && (
          <div role="alert" className="flex items-center justify-between gap-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <span>{queueError}</span>
            <button type="button" onClick={() => { void fetchQueue(); }} className="rounded border border-red-300 px-3 py-1 font-semibold hover:bg-red-100">
              Retry
            </button>
          </div>
        )}
        {successMessage && <div role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{successMessage}</div>}

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.35fr)_minmax(360px,0.9fr)]">
          <section aria-label="Official Circle Community reports" className="rounded-xl border border-gray-200 bg-white shadow-sm">
            {queueLoading && <div role="status" className="p-8 text-center text-sm text-gray-500">Loading Community reports…</div>}
            {!queueLoading && !queueError && queue && queue.items.length === 0 && (
              <div className="p-12 text-center text-sm text-gray-500">No reports match the selected filters.</div>
            )}
            {!queueLoading && !queueError && queue && queue.items.length > 0 && (
              <div className="divide-y divide-gray-100">
                {queue.items.map((report) => (
                  <button
                    key={report.report_id}
                    type="button"
                    onClick={() => selectReport(report.report_id)}
                    aria-pressed={selectedReportId === report.report_id}
                    className={`block w-full p-5 text-left transition-colors hover:bg-[#085041]/5 ${selectedReportId === report.report_id ? 'bg-[#085041]/5 ring-1 ring-inset ring-[#085041]/20' : ''}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-[#085041]">{report.circle.display_name}</p>
                        <p className="mt-1 text-xs text-gray-500">{report.circle.official_type} · {report.circle.club_id}</p>
                      </div>
                      <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-semibold capitalize text-gray-700">{label(report.status)}</span>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                      <div><p className="text-xs uppercase tracking-wide text-gray-400">Target</p><p className="mt-1 font-medium text-gray-700">{report.target_type}</p></div>
                      <div><p className="text-xs uppercase tracking-wide text-gray-400">Lifecycle</p><p className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold capitalize ${targetStatusClass(report.target?.status || 'unavailable')}`}>{label(report.target?.status || 'unavailable')}</p></div>
                      <div><p className="text-xs uppercase tracking-wide text-gray-400">Reason</p><p className="mt-1 font-medium capitalize text-gray-700">{label(report.reason)}</p></div>
                      <div><p className="text-xs uppercase tracking-wide text-gray-400">Reporter</p><p className="mt-1 truncate font-medium text-gray-700">{report.reporter.display_name}</p></div>
                    </div>
                    <p className="mt-4 text-xs text-gray-500">Reported {formatDate(report.created_at)}</p>
                  </button>
                ))}
              </div>
            )}
            {queue && queue.total > 0 && (
              <div className="flex items-center justify-between border-t border-gray-100 px-5 py-4 text-sm text-gray-600">
                <span>Page {queue.page} of {totalPages} · {queue.total} reports</span>
                <div className="flex gap-2">
                  <button type="button" disabled={page <= 1 || queueLoading} onClick={() => setPage((value) => value - 1)} className="rounded border border-gray-300 px-3 py-1.5 disabled:opacity-40">Previous</button>
                  <button type="button" disabled={page >= totalPages || queueLoading} onClick={() => setPage((value) => value + 1)} className="rounded border border-gray-300 px-3 py-1.5 disabled:opacity-40">Next</button>
                </div>
              </div>
            )}
          </section>

          <aside aria-label="Community report detail" className="rounded-xl border border-gray-200 bg-white shadow-sm">
            {!selectedReportId && <div className="p-8 text-center text-sm text-gray-500">Select a report to inspect its scoped target and review actions.</div>}
            {selectedReportId && detailLoading && <div role="status" className="p-8 text-center text-sm text-gray-500">Loading report detail…</div>}
            {selectedReportId && !detailLoading && detailError && (
              <div role="alert" className="m-5 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                <p>{detailError}</p>
                <button type="button" onClick={() => { void fetchDetail(selectedReportId); }} className="mt-3 rounded border border-red-300 px-3 py-1 font-semibold hover:bg-red-100">Retry detail</button>
              </div>
            )}
            {detail && !detailLoading && !detailError && (
              <div className="space-y-5 p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#8A6A38]">Report detail</p>
                    <h2 className="mt-1 text-xl font-semibold text-[#085041]">{detail.circle.display_name}</h2>
                    <p className="mt-1 text-xs text-gray-500">{detail.circle.official_type} · {detail.circle.club_id}</p>
                  </div>
                  <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-semibold capitalize text-gray-700">{label(detail.status)}</span>
                </div>

                <dl className="grid grid-cols-2 gap-3 rounded-lg bg-gray-50 p-4 text-sm">
                  <div><dt className="text-xs uppercase tracking-wide text-gray-400">Reason</dt><dd className="mt-1 font-medium capitalize text-gray-700">{label(detail.reason)}</dd></div>
                  <div><dt className="text-xs uppercase tracking-wide text-gray-400">Reporter</dt><dd className="mt-1 font-medium text-gray-700">{detail.reporter.display_name}</dd></div>
                  <div><dt className="text-xs uppercase tracking-wide text-gray-400">Created</dt><dd className="mt-1 text-gray-700">{formatDate(detail.created_at)}</dd></div>
                  <div><dt className="text-xs uppercase tracking-wide text-gray-400">Resolved</dt><dd className="mt-1 text-gray-700">{formatDate(detail.resolved_at)}</dd></div>
                </dl>

                <section aria-labelledby="community-target-heading" className="rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <h3 id="community-target-heading" className="font-semibold text-gray-800">Reported {detail.target_type}</h3>
                    <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold capitalize ${targetStatusClass(detail.target?.status || 'unavailable')}`}>{label(detail.target?.status || 'unavailable')}</span>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">Target ID: {detail.target_id}</p>
                  {!detail.target || detail.target.status === 'unavailable' ? (
                    <p className="mt-4 rounded bg-gray-50 p-3 text-sm text-gray-600">This target is unavailable or no longer exists. No moderation action can be taken against it.</p>
                  ) : (
                    <>
                      {detail.target.status === 'deleted' && <p className="mt-4 rounded bg-gray-50 p-3 text-sm text-gray-600">This content was deleted by its owner. Its body is not available for display.</p>}
                      {detail.target.status !== 'deleted' && detail.target.body && (
                        <p className="mt-4 whitespace-pre-wrap break-words rounded bg-gray-50 p-3 text-sm leading-6 text-gray-800">{detail.target.body}</p>
                      )}
                      {detail.target.author && <p className="mt-3 text-sm text-gray-600">Author: <span className="font-medium text-gray-800">{detail.target.author.display_name}</span></p>}
                      {detail.target.target_type === 'comment' && detail.target.parent_comment_id && <p className="mt-1 text-xs text-gray-500">Reply to comment: {detail.target.parent_comment_id}</p>}
                      {detail.target.target_type === 'comment' && detail.target.post_id && <p className="mt-1 text-xs text-gray-500">Post: {detail.target.post_id}</p>}
                    </>
                  )}
                </section>

                {reviewError && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{reviewError}</div>}
                {!reviewAction && (
                  <div className="flex flex-wrap gap-2">
                    {canDismiss && <button type="button" onClick={() => openReview('dismiss')} className="rounded-lg border border-gray-400 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">Dismiss</button>}
                    {canHide && <button type="button" onClick={() => openReview('hide')} className="rounded-lg bg-[#D85A30] px-3 py-2 text-sm font-semibold text-white hover:bg-[#b84725]">Hide</button>}
                    {canRestore && <button type="button" onClick={() => openReview('restore')} className="rounded-lg bg-[#085041] px-3 py-2 text-sm font-semibold text-white hover:bg-[#063d32]">Restore</button>}
                    {!canDismiss && !canHide && !canRestore && <p className="text-sm text-gray-500">No compatible moderation action is available for this current state.</p>}
                  </div>
                )}
                {reviewAction && (
                  <form onSubmit={submitReview} className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                    <h3 className="font-semibold capitalize text-gray-800">Confirm {reviewAction}</h3>
                    <p className="mt-2 text-sm text-gray-700">
                      {reviewAction === 'dismiss'
                        ? 'Dismiss this report without changing the target content.'
                        : reviewAction === 'hide'
                          ? 'Hide this active target from public Community reads. This changes content visibility.'
                          : 'Restore this hidden target to active public Community visibility.'}
                    </p>
                    <label htmlFor="community-moderation-note" className="mt-4 block text-sm font-semibold text-gray-800">Moderation note</label>
                    <textarea
                      id="community-moderation-note"
                      aria-label="Moderation note"
                      value={reviewNote}
                      maxLength={MAX_NOTE_LENGTH}
                      onChange={(event) => setReviewNote(event.target.value)}
                      disabled={reviewSubmitting}
                      className="mt-2 min-h-24 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[#085041] focus:outline-none focus:ring-2 focus:ring-[#085041]/20 disabled:bg-gray-100"
                    />
                    <p className="mt-1 text-right text-xs text-gray-500">{reviewNote.length}/{MAX_NOTE_LENGTH}</p>
                    <label className="mt-3 flex items-start gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={reviewConfirmed} onChange={(event) => setReviewConfirmed(event.target.checked)} disabled={reviewSubmitting} className="mt-0.5" />
                      <span>I confirm this {reviewAction} action applies to the currently loaded report state.</span>
                    </label>
                    <div className="mt-4 flex justify-end gap-2">
                      <button type="button" onClick={closeReview} disabled={reviewSubmitting} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 disabled:opacity-50">Cancel</button>
                      <button type="submit" disabled={reviewSubmitting} className="rounded-lg bg-[#085041] px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">{reviewSubmitting ? 'Submitting…' : `Confirm ${reviewAction}`}</button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default OfficialCircleCommunityModeration;
